import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconChecklist, IconChecks, IconEraser, IconFileExport, IconFiles } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import {
    extractPdfPages,
    getPdfPageCount,
    parsePageSelection,
    splitPdfEveryPage,
    zipPdfFiles,
} from './splitPdfEngine';
import { applyPdfPageEdits, pagesHaveEdits } from '../shared/pdfPageEditEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import PdfPageEditor from '../../components/pdf/PdfPageEditor';
import { usePdfPageEdits, fileKeyOf } from '../../components/pdf/usePdfPageEdits';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import SegmentedTabs from '../../image/shared/SegmentedTabs';
import Hint from '../../../../../components/common/Hint';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

export default function SplitPdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [pageCount, setPageCount] = useState(null);
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [mode, setMode] = useState('extract');
    const [pageInput, setPageInput] = useState('');
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const resetResult = useCallback(() => {
        setResult(null);
        setError(null);
        setProgress(null);
    }, []);

    const editFiles = useMemo(() => (file ? [file] : []), [file]);
    const { pagesByKey, updatePagesForKey, resetPageEdits } = usePdfPageEdits(editFiles, resetResult);
    const pages = file ? pagesByKey[fileKeyOf(file)] : null;
    const editable = Array.isArray(pages);
    const orderedPages = useMemo(
        () => (editable ? pages.filter((p) => !p.removed) : []),
        [editable, pages],
    );

    // Drop selections for pages that were removed / no longer exist after an edit.
    useEffect(() => {
        if (!editable) return;
        const validIds = new Set(orderedPages.map((p) => p.id));
        setSelectedIds((prev) => {
            let changed = false;
            const next = new Set();
            for (const id of prev) {
                if (validIds.has(id)) next.add(id);
                else changed = true;
            }
            return changed ? next : prev;
        });
    }, [editable, orderedPages]);

    const selectedCount = useMemo(
        () => orderedPages.filter((p) => selectedIds.has(p.id)).length,
        [orderedPages, selectedIds],
    );

    const loadFile = useCallback(async (incoming) => {
        setError(null);
        if (!incoming) return;
        if (!(incoming.type === 'application/pdf' || incoming.name.toLowerCase().endsWith('.pdf'))) {
            const msg = t('splitPdf.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (incoming.size > MAX_FILE_BYTES) {
            const msg = t('splitPdf.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setLoadingMeta(true);
        setFile(incoming);
        setResult(null);
        setPageInput('');
        setSelectedIds(new Set());
        try {
            const count = await getPdfPageCount(incoming);
            setPageCount(count);
        } catch (err) {
            setFile(null);
            setPageCount(null);
            const msg = err?.message === 'password'
                ? t('splitPdf.errors.password')
                : t('splitPdf.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setLoadingMeta(false);
        }
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in. This tool
    // holds one file, so a paste of several takes the first.
    usePdfPaste((files) => loadFile(files[0]));

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) loadFile(dropped);
    };

    const clearFile = () => {
        setFile(null);
        setPageCount(null);
        setPageInput('');
        setSelectedIds(new Set());
        setResult(null);
        setError(null);
        resetPageEdits();
    };

    const toggleSelect = (page) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(page.id)) next.delete(page.id);
            else next.add(page.id);
            return next;
        });
        resetResult();
    };

    const selectAll = () => {
        setSelectedIds(new Set(orderedPages.map((p) => p.id)));
        resetResult();
    };

    const clearSelection = () => {
        setSelectedIds(new Set());
        resetResult();
    };

    const handlePageInputChange = (value) => {
        setPageInput(value);
        setError(null);
        resetResult();
    };

    const hasExtractSelection = editable
        ? selectedCount > 0
        : !!pageInput.trim();

    const handleSplit = async () => {
        if (!file || !pageCount || processing) return;

        recordToolUsage('pdf/split', { category: 'pdf' });

        setProcessing(true);
        setResult(null);
        setError(null);

        try {
            const baseName = file.name.replace(/\.pdf$/i, '');

            if (mode === 'everyPage') {
                let parts;
                if (editable && pagesHaveEdits(pages)) {
                    parts = [];
                    for (let i = 0; i < orderedPages.length; i++) {
                        setProgress({ page: i + 1, total: orderedPages.length });
                        const blob = await applyPdfPageEdits(file, [orderedPages[i]]);
                        parts.push({ name: `${baseName}-page-${i + 1}.pdf`, blob });
                    }
                } else {
                    parts = await splitPdfEveryPage(file, (page, total) => {
                        setProgress({ page, total });
                    });
                }

                if (parts.length === 1) {
                    setResult({ name: parts[0].name, blob: parts[0].blob, kind: 'single' });
                } else {
                    const zipBlob = await zipPdfFiles(parts);
                    setResult({ name: `${baseName}-pages.zip`, blob: zipBlob, kind: 'zip', count: parts.length });
                }
            } else if (editable) {
                const selectedPages = orderedPages.filter((p) => selectedIds.has(p.id));
                if (!selectedPages.length) throw new Error('empty');
                const blob = await applyPdfPageEdits(file, selectedPages);
                setResult({
                    name: `${baseName}-extracted.pdf`,
                    blob,
                    kind: 'single',
                    count: selectedPages.length,
                });
            } else {
                const indices = parsePageSelection(pageInput, pageCount);
                const blob = await extractPdfPages(file, indices);
                setResult({
                    name: `${baseName}-extracted.pdf`,
                    blob,
                    kind: 'single',
                    count: indices.length,
                });
            }

            notifications.show({ title: t('common.success'), message: t('splitPdf.done'), color: 'green' });
        } catch (err) {
            let msg = t('splitPdf.errors.failed');
            if (err?.message === 'password') msg = t('splitPdf.errors.password');
            else if (err?.message === 'empty' || err?.message === 'removeAll') msg = t('splitPdf.errors.emptySelection');
            else if (err?.message === 'invalid' || err?.message === 'outOfRange') {
                msg = t('splitPdf.errors.invalidSelection', { total: toBnDigits(pageCount) });
            }
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const canSplit = file && pageCount && !loadingMeta && !processing
        && (mode === 'everyPage' || hasExtractSelection);

    const progressLabel = progress
        ? t('splitPdf.progress', {
            page: toBnDigits(progress.page),
            pages: toBnDigits(progress.total),
        })
        : '';

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!canSplit,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);

    const resultMeta = result
        ? (result.kind === 'zip'
            ? t('splitPdf.resultZip', { count: toBnDigits(result.count), size: formatFileSize(result.blob.size) })
            : t('splitPdf.resultSingle', {
                count: toBnDigits(result.count || 1),
                size: formatFileSize(result.blob.size),
            }))
        : undefined;
    const statusText = getToolStatusText(status, t, {
        success: t('splitPdf.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const progressPercent = progress?.total
        ? Math.round((progress.page / progress.total) * 100)
        : undefined;
    const fileMeta = file
        ? [
            formatFileSize(file.size),
            pageCount != null ? t('splitPdf.pageCount', { count: toBnDigits(pageCount) }) : null,
            loadingMeta ? t('splitPdf.reading') : null,
        ].filter(Boolean).join(' · ')
        : '';

    return (
        <ToolPageLayout
            slug="pdf/split"
            subtitleKey="splitPdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={!file}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={file || result || error ? clearFile : undefined}
            primaryAction={{
                label: t('splitPdf.split'),
                onClick: handleSplit,
                disabled: !canSplit || !!result,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={(e) => { loadFile(e.target.files?.[0]); e.target.value = ''; }}
                    />

                    {!file ? (
                        <PdfDropZone
                            title={t('splitPdf.dropTitle')}
                            hint={t('splitPdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <PdfUploadedFile
                            name={file.name}
                            meta={fileMeta}
                            onRemove={clearFile}
                            removeLabel={t('splitPdf.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file && pageCount != null && !loadingMeta ? (
                <ToolCard title={t('splitPdf.modeTitle')}>
                    <div className="space-y-3">
                        <SegmentedTabs
                            ariaLabel={t('splitPdf.modeTitle')}
                            stacked
                            value={mode}
                            onChange={(next) => { setMode(next); setError(null); resetResult(); }}
                            options={[
                                { id: 'extract', label: t('splitPdf.modeExtract'), icon: IconFileExport, hint: t('splitPdf.selectHint') },
                                { id: 'everyPage', label: t('splitPdf.modeEveryPage'), icon: IconFiles, hint: t('splitPdf.everyPageHint', { count: toBnDigits(pageCount) }) },
                            ]}
                        />

                        {mode === 'extract' && editable && (
                            <div className="space-y-2">
                                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                    {t('splitPdf.selectHint')}
                                </p>
                                {/* Icon over label, like the mode tabs above. Two actions and a live count. */}
                                <div className="grid grid-cols-3 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                    <Hint label={t('splitPdf.selectAllHint')}>
                                        <span className="flex min-w-0 [&>button]:w-full">
                                            <button
                                                type="button"
                                                onClick={selectAll}
                                                className="flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-[11px] font-medium leading-tight text-[#6B635C] transition-colors hover:bg-white hover:text-primary dark:text-zinc-400 dark:hover:bg-zinc-900"
                                            >
                                                <IconChecks size={20} stroke={1.8} />
                                                {t('splitPdf.selectAll')}
                                            </button>
                                        </span>
                                    </Hint>
                                    <Hint label={t('splitPdf.clearHint')}>
                                        <span className="flex min-w-0 [&>button]:w-full">
                                            <button
                                                type="button"
                                                onClick={clearSelection}
                                                disabled={selectedCount === 0}
                                                className="flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-[11px] font-medium leading-tight text-[#6B635C] transition-colors hover:bg-white hover:text-primary disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent disabled:hover:text-[#6B635C] dark:text-zinc-400 dark:hover:bg-zinc-900"
                                            >
                                                <IconEraser size={20} stroke={1.8} />
                                                {t('splitPdf.clearSelection')}
                                            </button>
                                        </span>
                                    </Hint>
                                    {/* Not a button: it only reports how many pages are in. */}
                                    <div
                                        aria-live="polite"
                                        className={`flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-[11px] font-semibold leading-tight ${
                                            selectedCount > 0
                                                ? 'bg-white text-primary shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900'
                                                : 'text-[#A8A099] dark:text-zinc-500'
                                        }`}
                                    >
                                        <IconChecklist size={20} stroke={1.8} />
                                        {t('splitPdf.selectedCount', { count: toBnDigits(selectedCount) })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {mode === 'extract' && !editable && (
                            <div className="space-y-1.5">
                                <label htmlFor="split-pages" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                    {t('splitPdf.pagesLabel')}
                                </label>
                                <input
                                    id="split-pages"
                                    type="text"
                                    value={pageInput}
                                    onChange={(e) => handlePageInputChange(e.target.value)}
                                    placeholder={t('splitPdf.pagesPlaceholder')}
                                    className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                />
                                <p className="text-xs text-zinc-400">{t('splitPdf.pagesHint', { total: toBnDigits(pageCount) })}</p>
                            </div>
                        )}

                        {mode === 'everyPage' && (
                            <p className="text-xs text-zinc-400">{t('splitPdf.everyPageHint', { count: toBnDigits(pageCount) })}</p>
                        )}
                    </div>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={progressLabel || t('splitPdf.splitting')}
                    progress={progressPercent}
                    error={error}
                    successLabel={resultMeta}
                    downloadLabel={t('splitPdf.download')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onPrint={result && /\.pdf$/i.test(result.name) ? () => printBlob(result.blob) : undefined}
                    onEditAgain={result ? resetResult : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={result ? clearFile : undefined}
                    againLabel={t('splitPdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('splitPdf.split')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                    pdfPreview={!!file && pageCount != null && !processing && !error}
                >
                    {file && pageCount != null && !processing && !error ? (
                        result?.kind === 'single' ? (
                            <PdfDocPreview source={result.blob} />
                        ) : !result ? (
                            <PdfPageEditor
                                files={editFiles}
                                pagesByKey={pagesByKey}
                                onPagesChangeForKey={updatePagesForKey}
                                activeIndex={0}
                                onActiveIndexChange={() => {}}
                                selectable={mode === 'extract'}
                                isSelected={(page) => selectedIds.has(page.id)}
                                onToggleSelect={toggleSelect}
                            />
                        ) : null
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
